import React from 'react';

import { compact, filter, find, includes, isEmpty, isNil, map, noop, uniq } from 'lodash';

import type { JobHiringAttendance } from './type';

// Helper functions
const filterSelectedAttendances = (attendances: JobHiringAttendance[], selectedIds: Set<AttendancesId>) =>
  filter(attendances, ({ id }) => selectedIds.has(id));

const getAttendanceJobIds = (attendances: JobHiringAttendance[]) => {
  const jobIds = map(attendances, (attendance) => attendance.jobHiring?.job?.id);
  return uniq(compact(jobIds));
};

const isAttendanceSelectable = (attendance: JobHiringAttendance, selectedAttendances: JobHiringAttendance[]) => {
  // If no attendance is selected, allow selecting any attendance
  if (isEmpty(selectedAttendances)) return true;

  // If the attendance is already selected, allow deselection
  if (includes(map(selectedAttendances, 'id'), attendance.id)) return true;

  const attendanceJobId = attendance.jobHiring?.job?.id;
  if (isNil(attendanceJobId)) return false;

  const selectedJobIds = getAttendanceJobIds(selectedAttendances);
  return includes(selectedJobIds, attendanceJobId);
};

type AttendancesId = JobHiringAttendance['id'];

export type AttendancesPageContextType = {
  attendances: JobHiringAttendance[];
  setAttendances: React.Dispatch<React.SetStateAction<AttendancesPageContextType['attendances']>>;

  selectedAttendancesIds: Set<AttendancesId>;
  selectedAttendances: JobHiringAttendance[];
  selectAttendance: (attendanceId: AttendancesId, selected: boolean) => void;
  canSelectAttendance: (attendanceId: AttendancesId) => boolean;
  setSelectedAttendancesIds: React.Dispatch<React.SetStateAction<AttendancesPageContextType['selectedAttendancesIds']>>;

  currentAttendance: JobHiringAttendance | null;
  currentAttendanceId: AttendancesId | null;
  setCurrentAttendanceId: React.Dispatch<React.SetStateAction<AttendancesPageContextType['currentAttendanceId']>>;

  openBatchModal: boolean;
  setOpenBatchModal: React.Dispatch<React.SetStateAction<boolean>>;
  hasUnsavedChanges: boolean;
  setDetailPageDirty: React.Dispatch<React.SetStateAction<boolean>>;
  setBatchDrawerDirty: React.Dispatch<React.SetStateAction<boolean>>;
};

const AttendancesPageContext = React.createContext<AttendancesPageContextType>({
  attendances: [],
  setAttendances: noop,

  selectedAttendancesIds: new Set(),
  selectedAttendances: [],
  selectAttendance: noop,
  canSelectAttendance: () => true,
  setSelectedAttendancesIds: noop,

  currentAttendance: null,
  currentAttendanceId: null,
  setCurrentAttendanceId: noop,

  openBatchModal: false,
  setOpenBatchModal: noop,
  hasUnsavedChanges: false,
  setDetailPageDirty: noop,
  setBatchDrawerDirty: noop,
});

// --- Reducer ---

type State = {
  attendances: JobHiringAttendance[];
  selectedAttendancesIds: Set<AttendancesId>;
  currentAttendanceId: AttendancesId | null;
};

type Action =
  | {
      type: 'SET_ATTENDANCES';
      payload: JobHiringAttendance[] | ((prev: JobHiringAttendance[]) => JobHiringAttendance[]);
    }
  | {
      type: 'SET_SELECTED_IDS';
      payload: Set<AttendancesId> | ((prev: Set<AttendancesId>) => Set<AttendancesId>);
    }
  | {
      type: 'SELECT_ATTENDANCE';
      attendanceId: AttendancesId;
      selected: boolean;
    }
  | {
      type: 'SET_CURRENT_ATTENDANCE_ID';
      payload: AttendancesId | null | ((prev: AttendancesId | null) => AttendancesId | null);
    };

const initialState: State = {
  attendances: [],
  selectedAttendancesIds: new Set(),
  currentAttendanceId: null,
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SET_ATTENDANCES': {
      const next = typeof action.payload === 'function' ? action.payload(state.attendances) : action.payload;
      return { ...state, attendances: next };
    }
    case 'SET_SELECTED_IDS': {
      const next = typeof action.payload === 'function' ? action.payload(state.selectedAttendancesIds) : action.payload;
      return { ...state, selectedAttendancesIds: next };
    }
    case 'SELECT_ATTENDANCE': {
      const attendance = find(state.attendances, ({ id }) => id === action.attendanceId);
      if (isNil(attendance)) return state;

      const selectedAttendances = filterSelectedAttendances(state.attendances, state.selectedAttendancesIds);
      const isSelected = state.selectedAttendancesIds.has(action.attendanceId);
      const canSelect = isAttendanceSelectable(attendance, selectedAttendances);
      if (!isSelected && !canSelect) {
        return state;
      }

      const newSet = new Set(state.selectedAttendancesIds);
      if (action.selected) {
        newSet.add(action.attendanceId);
      } else {
        newSet.delete(action.attendanceId);
      }

      return { ...state, selectedAttendancesIds: newSet };
    }
    case 'SET_CURRENT_ATTENDANCE_ID': {
      const next = typeof action.payload === 'function' ? action.payload(state.currentAttendanceId) : action.payload;
      return { ...state, currentAttendanceId: next };
    }
    default:
      return state;
  }
};

// --- Provider ---

const AttendancesPageContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, dispatch] = React.useReducer(reducer, initialState);

  // Batch actions state
  const [openBatchModal, setOpenBatchModal] = React.useState(false);
  const [detailPageDirty, setDetailPageDirty] = React.useState(false);
  const [batchDrawerDirty, setBatchDrawerDirty] = React.useState(false);

  // Combine dirty flags from both forms
  const hasUnsavedChanges = detailPageDirty || batchDrawerDirty;

  const setAttendances = React.useCallback<React.Dispatch<React.SetStateAction<JobHiringAttendance[]>>>(
    (payload) => dispatch({ type: 'SET_ATTENDANCES', payload }),
    [],
  );

  const setSelectedAttendancesIds = React.useCallback<React.Dispatch<React.SetStateAction<Set<AttendancesId>>>>(
    (payload) => dispatch({ type: 'SET_SELECTED_IDS', payload }),
    [],
  );

  const setCurrentAttendanceId = React.useCallback<React.Dispatch<React.SetStateAction<AttendancesId | null>>>(
    (payload) => dispatch({ type: 'SET_CURRENT_ATTENDANCE_ID', payload }),
    [],
  );

  const selectAttendance = React.useCallback((attendanceId: AttendancesId, selected: boolean) => {
    dispatch({ type: 'SELECT_ATTENDANCE', attendanceId, selected });
  }, []);

  const currentAttendance = React.useMemo(
    () => find(state.attendances, ({ id }) => id === state.currentAttendanceId) ?? null,
    [state.attendances, state.currentAttendanceId],
  );

  const selectedAttendances = React.useMemo(
    () => filterSelectedAttendances(state.attendances, state.selectedAttendancesIds),
    [state.attendances, state.selectedAttendancesIds],
  );

  const canSelectAttendance = React.useCallback(
    (attendanceId: AttendancesId) => {
      const attendance = find(state.attendances, ({ id }) => id === attendanceId);
      if (isNil(attendance)) return false;

      return isAttendanceSelectable(attendance, selectedAttendances);
    },
    [selectedAttendances],
  );

  const values = React.useMemo(
    () => ({
      ...state,
      setAttendances,

      selectedAttendances,
      selectAttendance,
      canSelectAttendance,
      setSelectedAttendancesIds,

      currentAttendance,
      setCurrentAttendanceId,

      openBatchModal,
      setOpenBatchModal,
      hasUnsavedChanges,
      setDetailPageDirty,
      setBatchDrawerDirty,
    }),
    [
      state,
      setAttendances,

      selectedAttendances,
      selectAttendance,
      canSelectAttendance,
      setSelectedAttendancesIds,

      currentAttendance,
      setCurrentAttendanceId,

      openBatchModal,
      setOpenBatchModal,
      hasUnsavedChanges,
      detailPageDirty,
      batchDrawerDirty,
      setDetailPageDirty,
      setBatchDrawerDirty,
    ],
  );

  return <AttendancesPageContext.Provider value={values}>{children}</AttendancesPageContext.Provider>;
};

export { AttendancesPageContext, AttendancesPageContextProvider };
